import React from "react";
import { CompanySummaryProps } from "../../../../types/cdd-records.types";
import { formatDateShort } from "../../../../utils/dateFormatters";
import {
    getRiskBadgeClass,
    getVerificationBadgeClass,
} from "./companySectionConfig";

/**
 * Format raw company type codes into human-readable text
 * e.g., "APTY|LMSH|PROP" -> "Australian Proprietary Company"
 */
const formatCompanyType = (rawType?: string): string => {
  if (!rawType) return "-";
  
  // Map of common ASIC entity type codes and trust abbreviations
  const typeMap: Record<string, string> = {
    // ASIC company types
    'APTY': 'Australian Proprietary Company',
    'APUB': 'Australian Public Company',
    'REGT': 'Registered Body',
    'FNOS': 'Foreign Company',
    'LMSH': 'Limited by Shares',
    'LMGT': 'Limited by Guarantee',
    'UNLM': 'Unlimited',
    'NLIA': 'No Liability',
    'PROP': 'Proprietary',
    'PUBM': 'Public',
    // Trust types
    'DTT': 'Discretionary Trading Trust',
    'DIT': 'Discretionary Investment Trust',
    'DFT': 'Discretionary Family Trust',
    'HDT': 'Hybrid Discretionary Trust',
    'UNT': 'Unit Trust',
    'FUT': 'Fixed Unit Trust',
    'PUT': 'Public Unit Trust',
    'MUT': 'Managed Unit Trust',
    'LUT': 'Listed Unit Trust',
    'SMF': 'Self-Managed Superannuation Fund',
    'SMSF': 'Self-Managed Superannuation Fund',
    'TRT': 'Trading Trust',
    'TST': 'Testamentary Trust',
    'BRT': 'Bare Trust',
    'CST': 'Charitable Trust',
    'PST': 'Public Sector Trust',
    'DST': 'Discretionary Trust',
    'FST': 'Fixed Trust',
    'HYT': 'Hybrid Trust',
    'SPT': 'Special Purpose Trust',
    'DET': 'Deceased Estate Trust',
    'PRT': 'Property Trust',
    'RIT': 'Registered Investment Trust',
    'MIT': 'Managed Investment Trust',
    'CCT': 'Common Law Trust',
    'SFT': 'Superannuation Fund Trust',
    'APF': 'APRA-Regulated Superannuation Fund',
    'SAF': 'Small APRA Fund',
    'PSF': 'Pooled Superannuation Fund',
    'RSA': 'Retirement Savings Account',
    'ABT': 'Aboriginal Trust',
    'CLT': 'Charitable Lead Trust',
    'CRT': 'Charitable Remainder Trust',
    'LPT': 'Listed Property Trust',
    'CPT': 'Commercial Property Trust',
    'RPT': 'Residential Property Trust',
    'IFT': 'Infrastructure Trust',
    'AGT': 'Agricultural Trust',
    // Other entity types
    'PTY': 'Proprietary',
    'LTD': 'Limited',
    'INC': 'Incorporated',
    'PTR': 'Partnership',
    'SOL': 'Sole Trader',
    'OIE': 'Other Incorporated Entity',
    'PRV': 'Australian Private Company',
    'COP': 'Co-operative',
    'ASN': 'Association',
    'GOV': 'Government Entity',
    'STB': 'Statutory Body',
    'JVT': 'Joint Venture',
    'NFP': 'Not-for-Profit',
  };
  
  // Split by pipe and try to find meaningful parts
  const parts = rawType.split('|').map(p => p.trim().toUpperCase());
  
  // Try to find the main company type (APTY, APUB, etc.)
  const mainType = parts.find(p => ['APTY', 'APUB', 'REGT', 'FNOS'].includes(p));
  if (mainType && typeMap[mainType]) {
    return typeMap[mainType];
  }
  
  // If we can map any part, use the first mapped value
  for (const part of parts) {
    if (typeMap[part]) {
      return typeMap[part];
    }
  }
  
  // Fallback: return the raw value cleaned up
  return rawType.replace(/\|/g, ' / ');
};

export const CompanySummary: React.FC<CompanySummaryProps> = ({
  profile,
  riskLevel,
  verificationStatus,
}) => {
  return (
    <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50 rounded-xl shadow-lg border border-slate-200/60 overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-200/60 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800">
        <h3 className="text-lg font-semibold text-white">Company Summary</h3>
      </div>

      <div className="p-6">
        {/* Key Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-500 uppercase">
              Basic Information
            </h4>
            <dl className="space-y-3">
              <div>
                <dt className="text-xs text-gray-500">Legal Name</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {profile.legalName}
                </dd>
              </div>
              {profile.tradingName && (
                <div>
                  <dt className="text-xs text-gray-500">Trading Name</dt>
                  <dd className="text-sm text-gray-900">
                    {profile.tradingName}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-xs text-gray-500">Company Type</dt>
                <dd className="text-sm text-gray-900">
                  {formatCompanyType(profile.registration.companyType)}
                  {profile.registration.companyClass &&
                    ` (${profile.registration.companyClass})`}
                </dd>
              </div>
            </dl>
          </div>

          {/* Registration Details */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-500 uppercase">
              Registration
            </h4>
            <dl className="space-y-3">
              {(profile.registration.abn || profile.registration.acn) && (
                <div>
                  <dt className="text-xs text-gray-500">ABN/ACN</dt>
                  <dd className="text-sm font-mono text-gray-900">
                    {profile.registration.abn || profile.registration.acn}
                  </dd>
                </div>
              )}
              {profile.registration.arbn && (
                <div>
                  <dt className="text-xs text-gray-500">ARBN</dt>
                  <dd className="text-sm font-mono text-gray-900">
                    {profile.registration.arbn}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-xs text-gray-500">Date of Incorporation</dt>
                <dd className="text-sm text-gray-900">
                  {formatDateShort(profile.dateOfIncorporation)}
                </dd>
              </div>
            </dl>
          </div>

          {/* Risk & Verification */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-500 uppercase">
              Risk & Verification
            </h4>
            <dl className="space-y-3">
              <div>
                <dt className="text-xs text-gray-500">Risk Level</dt>
                <dd>
                  <span
                    className={`inline-block px-3 py-1 rounded text-sm font-medium capitalize ${getRiskBadgeClass(riskLevel)}`}
                  >
                    {riskLevel}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Verification Status</dt>
                <dd>
                  <span
                    className={`inline-block px-3 py-1 rounded text-sm font-medium capitalize ${getVerificationBadgeClass(verificationStatus)}`}
                  >
                    {verificationStatus.replace("-", " ")}
                  </span>
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Addresses */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Registered Address */}
          <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl border border-slate-200/40">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <svg
                className="w-4 h-4 text-slate-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              Registered Address
            </h4>
            <address className="text-sm text-slate-600 not-italic">
              {profile.registeredAddress.line1}
              <br />
              {profile.registeredAddress.line2 && (
                <>
                  {profile.registeredAddress.line2}
                  <br />
                </>
              )}
              {profile.registeredAddress.suburb},{" "}
              {profile.registeredAddress.state}{" "}
              {profile.registeredAddress.postcode}
              <br />
              {profile.registeredAddress.country}
            </address>
          </div>

          {/* Principal Place of Business */}
          {profile.principalPlaceOfBusiness && (
            <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl border border-slate-200/40">
              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-slate-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                Principal Place of Business
              </h4>
              <address className="text-sm text-slate-600 not-italic">
                {profile.principalPlaceOfBusiness.line1}
                <br />
                {profile.principalPlaceOfBusiness.line2 && (
                  <>
                    {profile.principalPlaceOfBusiness.line2}
                    <br />
                  </>
                )}
                {profile.principalPlaceOfBusiness.suburb},{" "}
                {profile.principalPlaceOfBusiness.state}{" "}
                {profile.principalPlaceOfBusiness.postcode}
                <br />
                {profile.principalPlaceOfBusiness.country}
              </address>
            </div>
          )}
        </div>

        {/* Contact Details */}
        {profile.contactDetails && (
          <div className="mt-6 p-4 bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl border border-slate-200/40">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <svg
                className="w-4 h-4 text-slate-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              Contact Details
            </h4>
            <div className="flex flex-wrap gap-6 text-sm">
              {profile.contactDetails.email && (
                <div>
                  <span className="text-gray-500">Email:</span>
                  <a
                    href={`mailto:${profile.contactDetails.email}`}
                    className="ml-2 text-blue-600 hover:underline"
                  >
                    {profile.contactDetails.email}
                  </a>
                </div>
              )}
              {profile.contactDetails.phone && (
                <div>
                  <span className="text-gray-500">Phone:</span>
                  <a
                    href={`tel:${profile.contactDetails.phone}`}
                    className="ml-2 text-blue-600 hover:underline"
                  >
                    {profile.contactDetails.phone}
                  </a>
                </div>
              )}
              {profile.contactDetails.fax && (
                <div>
                  <span className="text-gray-500">Fax:</span>
                  <span className="ml-2 text-gray-900">
                    {profile.contactDetails.fax}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanySummary;
